import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import EditProductItemForm from '../EditProductItemForm';
import type { SendProduct, Product, UploadImageParams } from 'types/Products';
import { handleSuccess, handleError } from 'utils/index';
import {
  CREATE_MESSAGE,
  REFERENCE_EXISTS_LINE_ONE_MESSAGE,
  REFERENCE_EXISTS_LINE_TWO_MESSAGE
} from 'const/Message';
import Loader from 'components/Loader';
import type { Catalog } from 'types/Catalog';
import { createProduct, uploadImageV2 } from 'services/productService';
import {
  CreatePromotionParams,
  createPromotion
} from 'services/promotionService';
import { CHANNELS_PROMOTION } from 'const/Const';
import { handleMessageWhenAppliedPromotion } from 'utils/handleResponse';

interface IProps {
  onClose: (
    newPro?: any,
    categoryId?: string,
    mode?: 'create' | 'edit'
  ) => void;
  categoryCreate: string;
  catalog: Catalog;
}

const REFERENCE_EXISTS_MESSAGE = (
  <>
    <div>{REFERENCE_EXISTS_LINE_ONE_MESSAGE}</div>
    <div>{REFERENCE_EXISTS_LINE_TWO_MESSAGE}</div>
  </>
);

const AddProductItem = ({ onClose, categoryCreate }: IProps) => {
  const queryClient = useQueryClient();

  const { isLoading: isCreatingProduct, mutate: onCreateProduct } = useMutation(
    (product: SendProduct) => createProduct(product)
  );

  const { isLoading: isCreatingPromotion, mutateAsync: mutateCreatePromotion } =
    useMutation((data: CreatePromotionParams) => createPromotion(data));
  const { isLoading: isUploadingImage, mutateAsync: mutateUploadImage } =
    useMutation(({ code, formData }: UploadImageParams) =>
      uploadImageV2(code, formData)
    );

  const onCreate = (product: Product, imgCropped: any) => {
    const data: SendProduct = {
      name: product.name,
      isOption: false,
      maxAmount: product.maxAmount,
      description: product.description,
      categories: product.categories,
      includedTax: product.includedTax,
      isAlcoholicProduct: product.isAlcoholicProduct,
      dietaryAttributes: product.dietaryAttributes,
      groupOptionIRIs: product.groupOptionIRIs,
      sellingAsOption: product.sellingAsOption,
      allergenAttributes: product.allergenAttributes,
      notMultipleQuantityOption: product.notMultipleQuantityOption,
      isSellWithUnits: product.isSellProductWithUnits,
      ...(product.isSellProductWithUnits
        ? {
            pricePerUnit: Math.round(product.pricePerUnit!),
            quantityProductSoldByWeight: product.quantityProductSoldByWeight,
            unit: product.unit
          }
        : { price: Math.round(product.price!) })
    };

    if (product.reference) {
      data.reference = product.reference;
    }

    const fd = new FormData();

    if (imgCropped) {
      fd.append('files[]', imgCropped);
    }

    if (!imgCropped && product.files) {
      for (let i = 0; i < product.files.length; i++) {
        fd.append('files[]', product.files[i]);
      }
    }

    onCreateProduct(data, {
      onSuccess: async (res) => {
        handleSuccess(CREATE_MESSAGE.PRODUCT_SUCCESS);

        let promotionData;

        if (product.startDate && product.endDate && product.isReduction) {
          promotionData = {
            channels: [CHANNELS_PROMOTION],
            name: `Discount percent ${product.reductionPercent}`,
            code: `discount-percent-${Math.floor(
              product.reductionPercent || 0
            )}-${uuidv4()}`,
            startDate: product.startDate,
            endDate: product.endDate,
            priority: 1,
            exclusive: true,
            enabled: true,
            scopes: [
              {
                type: 'for_variants',
                configuration: {
                  variants: [res.data.variants[0].code as string]
                }
              }
            ],
            actions: [
              {
                type: 'percentage_discount',
                configuration: {
                  amount: product.reductionPercent || 0
                }
              }
            ]
          };
        }

        if (fd.getAll('files[]').length <= 0) {
          if (product.isReduction && promotionData) {
            mutateCreatePromotion(promotionData, {
              onSuccess: () => {
                handleMessageWhenAppliedPromotion();
              }
            });
          }

          queryClient.invalidateQueries('getListGroupOptions');
          queryClient.invalidateQueries('getListProducts');
          queryClient.invalidateQueries('getListTaxon');
          queryClient.invalidateQueries('getListCategories');
          queryClient.invalidateQueries(['getProducts']);

          onClose(res.data, categoryCreate, 'create');

          return;
        }

        const listApi = [
          mutateUploadImage({ code: res.data.code, formData: fd })
        ];

        if (product.isReduction && promotionData) {
          listApi.push(mutateCreatePromotion(promotionData));
        }

        await Promise.all(listApi);

        queryClient.invalidateQueries('getListProducts');
        queryClient.invalidateQueries(['getProducts']);

        if (product.isReduction) {
          handleMessageWhenAppliedPromotion();
        }

        onClose();
      },
      onError: (error: any) => {
        handleError(
          error.response.status,
          error.response.status === 422
            ? REFERENCE_EXISTS_MESSAGE
            : CREATE_MESSAGE.PRODUCT_ERROR
        );
      }
    });
  };

  return (
    <>
      {isCreatingProduct || isUploadingImage || isCreatingPromotion ? (
        <Loader />
      ) : null}
      <EditProductItemForm
        create={onCreate}
        onClose={onClose}
        categoryCreate={categoryCreate}
        mode="create"
      />
    </>
  );
};

export default AddProductItem;
